import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, CalendarCheck, Users,
  Settings2, LogOut, PanelLeftClose, PanelLeftOpen, Shield,
  Cable
} from "lucide-react";
import axios from "axios";
import { useSuperAdmin } from "./SuperAdminContext";

const NAV = [
  { section: "Principal" },
  { to: "/superadmin",              label: "Dashboard",    icon: LayoutDashboard, end: true },
  { divider: true },
  { section: "Plateforme" },
  { to: "/superadmin/hotels",       label: "Hôtels",       icon: Building2 },
  { to: "/superadmin/reservations", label: "Réservations", icon: CalendarCheck },
  { to: "/superadmin/users",        label: "Utilisateurs", icon: Users },
  { divider: true },
  { section: "Système" },
  { to: "/superadmin/platform",     label: "Plateforme",   icon: Settings2 },
  { to: "/superadmin/connectivity", label: "Connectivité", icon: Cable },
];

export default function SuperAdmin() {
  const navigate = useNavigate();
  const { superAdmin, setSuperAdmin } = useSuperAdmin();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    try {
      await axios.post("https://api.komorotel.com/api/superadmin/logout", {}, { withCredentials: true });
    } catch (_) {}
    setSuperAdmin(null);
    navigate("/superadmin/login");
  };

  const initials = (superAdmin?.name || "SA")
    .split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="flex h-screen overflow-hidden bg-[#f9fafb]" style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── SIDEBAR ─────────────────────────────────────────── */}
      <div className={`flex flex-col bg-white border-r border-[#e5e7eb] transition-all duration-150 shrink-0 ${collapsed ? "w-16" : "w-60"}`}>

        {/* Logo */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-[#e5e7eb]">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gray-900 flex items-center justify-center shrink-0">
                <Shield className="text-white" size={14} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 leading-tight">Komorotel</p>
                <p className="text-[10px] text-gray-400">Super Admin</p>
              </div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(c => !c)}
            className="w-7 h-7 flex items-center justify-center rounded-lg border border-[#e5e7eb] text-gray-400 hover:bg-gray-50 transition shrink-0"
          >
            {collapsed ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 flex flex-col gap-0.5 px-2 py-2 overflow-y-auto">
          {NAV.map((item, i) => {
            if (item.divider) {
              return <div key={i} className="h-px bg-gray-100 my-1 mx-1" />;
            }
            if (item.section) {
              return !collapsed
                ? <p key={i} className="text-[10px] font-medium text-gray-400 uppercase tracking-widest px-2 pt-2 pb-1">{item.section}</p>
                : null;
            }
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-2.5 rounded-lg text-sm transition-all duration-150 text-left w-full h-9
                  ${isActive
                    ? "bg-[#eaf3de] text-[#3B6D11] font-medium"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                  }`
                }
              >
                <Icon size={17} className="shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-[#e5e7eb] px-2 py-2">
          {!collapsed && (
            <div className="flex items-center gap-2.5 px-2.5 py-2 mb-1">
              <div className="w-7 h-7 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-medium shrink-0">
                {initials}
              </div>
              <div className="overflow-hidden">
                <p className="text-xs font-medium text-gray-900 truncate">{superAdmin?.name || "Super Admin"}</p>
                <p className="text-xs text-gray-400 truncate">{superAdmin?.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            title={collapsed ? "Déconnexion" : undefined}
            className="flex items-center gap-2.5 px-2.5 h-9 rounded-lg text-sm text-red-400 hover:bg-red-50 hover:text-red-600 transition w-full"
          >
            <LogOut size={17} className="shrink-0" />
            {!collapsed && <span>Déconnexion</span>}
          </button>
        </div>
      </div>

      {/* ── MAIN ────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
